<template>
  <div class="space-y-3 sm:space-y-4">
    <!-- Order Type -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {{ $t('app.views.orders.modals.new_order.order_type') }}
      </label>
      <BaseFilterButtons
        :filter-groups="orderTypeFilterGroups"
        :filter-values="{ orderType: modelValue.type }"
        size="lg"
        @filter-click="handleOrderTypeFilter"
      />
    </div>

    <!-- Table Selection (Dine-in) -->
    <div v-if="modelValue.type === 'Dine-in'">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {{ $t('app.views.orders.modals.new_order.table') }}
      </label>
      <BaseFilterButtons
        :filter-groups="tableFilterGroups"
        :filter-values="{ tableId: modelValue.tableId?.toString() }"
        size="lg"
        @filter-click="handleTableFilter"
      />
    </div>

    <!-- Customer information is now handled by CustomerSelector component -->
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { OrderFormData } from '@/composables/useOrderForm';
import BaseFilterButtons from '@/components/common/BaseFilterButtons.vue';

interface Table {
  id: number;
  number: number;
  capacity: number;
}

interface Props {
  modelValue: any; // Accept any form type to avoid type conflicts
  tables: Table[];
  loading: boolean | string | null;
  error: boolean | string | null;
  allowDineInWithoutTable?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: OrderFormData];
}>();

const { t } = useI18n();

// Sort tables by number in ascending order
const sortedTables = computed(() => {
  return [...props.tables].sort((a, b) => a.number - b.number);
});

// Order type filter groups for BaseFilterButtons
const orderTypeFilterGroups = computed(() => [
  {
    name: 'orderType',
    options: [
      { value: 'Dine-in', label: t('app.views.orders.modals.new_order.dine_in') },
      { value: 'Takeaway', label: t('app.views.orders.modals.new_order.takeaway') },
      { value: 'Delivery', label: t('app.views.orders.modals.new_order.delivery') }
    ],
    allowToggle: false // Don't allow toggling off - always need a selection
  }
]);

// Table filter groups for BaseFilterButtons
const tableFilterGroups = computed(() => {
  const options: { value: string; label: string }[] = [];
  
  // Add table options (no explicit "Cualquiera" option)
  if (props.loading) {
    options.push({ value: 'loading', label: '...' });
  } else if (props.error && props.tables.length === 0) {
    options.push({ value: 'error', label: 'Error' });
  } else if (props.tables.length === 0 && !props.allowDineInWithoutTable) {
    options.push({ value: 'empty', label: 'Sin mesas' });
  } else {
    options.push(...sortedTables.value.map(table => ({
      value: table.id.toString(),
      label: `Mesa ${table.number}`
    })));
  }
  
  return [
    {
      name: 'tableId',
      options,
      allowToggle: true // Allow toggling off - no selection means "cualquiera"
    }
  ];
});

// Handle order type filter click
const handleOrderTypeFilter = (groupName: string, value: string | number | undefined) => {
  if (groupName === 'orderType' && value) {
    emit('update:modelValue', { ...props.modelValue, type: value as any });
  }
};

// Handle table filter click
const handleTableFilter = (groupName: string, value: string | number | undefined) => {
  if (groupName === 'tableId') {
    // Convert empty string to null for "No Table" option
    const tableId = value === '' ? null : (value ? Number(value) : null);
    emit('update:modelValue', { ...props.modelValue, tableId });
  }
};



</script>
